import { z } from 'zod';

/* Defensive coercion — see saleStockSchemas note. */
const num = () => z.coerce.number();
const numNull = () => z.union([z.coerce.number(), z.null()]);

export const StoreBrandSummarySchema = z.array(
  z.object({
    week: z.string(),
    facility_name: z.string(),
    brand_name: z.string().nullable(),
    merch_portfolio: z.string().nullable(),
    brand_manager: z.string().nullable(),
    gross_gmv: num().optional(),
    prev_week_gross_gmv: numNull().optional(),
    three_week_avg_gross_gmv: numNull().optional(),
    net_gmv: num(),
    prev_week_net_gmv: numNull(),
    three_week_avg_net_gmv: numNull(),
    wow_pct: numNull(),
    stock: num(),
    unique_styles: num(),
    styles_3_size_ml: num(),
    /* Brand-grain distinct-style counts (added 2026-05-14). Optional until the
       mart rebuild lands — frontend falls back to per-store rollup if absent. */
    brand_unique_styles: num().optional(),
    brand_styles_3_size_ml: num().optional(),
    three_size_ml_pct: num(),
    front_inv: num(),
    qty_sold: num(),
    /* Ledger-sourced quantities (added 2026-05-13). Optional until the mart roll
       is applied. */
    gross_qty: num().optional(),
    net_qty: num().optional(),
    weekly_run_rate: numNull(),
    stock_cover_weeks: numNull(),
    rev_per_hanger: numNull(),
    prev_week_rev_per_hanger: numNull(),
    three_week_avg_rev_per_hanger: numNull(),
    avg_size_depth: num(),
    return_qty: num(),
    return_rate_pct: num(),
    /* Mart additions (2026-05-27) for Avg Net Qty W-1, Total Stock WoW % and
       % Styles Not in Display. Optional so older cached card responses still
       parse cleanly. */
    prev_week_net_qty: numNull().optional(),
    prev_week_stock: numNull().optional(),
    styles_in_display: num().optional(),
    /* Live inventory metrics (added 2026-06-10, card 8126) sourced from
       mart_latest_warehouse_inventory — current stock state rather than the
       Monday-of-week snapshot. Optional so older cached responses parse. */
    live_stock: num().optional(),
    live_front_inv: num().optional(),
    live_avg_size_depth: num().optional(),
    live_unique_styles: num().optional(),
    live_styles_3_size_ml: num().optional(),
    live_styles_in_display: num().optional(),
    live_styles_3plus_sizes: num().optional(),
    live_brand_unique_styles: num().optional(),
    live_brand_styles_3_size_ml: num().optional(),
    live_brand_styles_3plus_sizes: num().optional(),
  }),
);
export type StoreBrandSummary = z.infer<typeof StoreBrandSummarySchema>;

/* Brand MTD Net GMV at date grain (card 8127). Replaces the week-grain misuse
   of the summary card for the MTD columns — toStartOfWeek() filtering pulled
   whole weeks, overstating MTD windows that start or end mid-week. */
export const BrandMtdNetGmvSchema = z.array(
  z.object({
    brand_name: z.string().nullable(),
    net_gmv: num(),
    gross_gmv: num().optional(),
  }),
);
export type BrandMtdNetGmv = z.infer<typeof BrandMtdNetGmvSchema>;

/* All Styles Sold or In Stock — per-store split + issue-sticker signals
   (card 8128). Grain: (lk_style_number, store_name); a row appears when the
   style sold there in the window OR holds live stock there. Style metadata is
   carried so stock-only styles can render without a second lookup. */
export const StyleStoreSplitSchema = z.array(
  z.object({
    lk_style_number: z.string().nullable(),
    store_name: z.string(),
    brand_name: z.string().nullable().optional(),
    product_name: z.string().nullable().optional(),
    image: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    brand_manager: z.string().nullable().optional(),
    merch_portfolio: z.string().nullable().optional(),
    qty_sold: num(),
    revenue: num(),
    live_stock: num(),
    live_front_inv: num(),
    sizes_in_stock: num(),
    has_m: num(),
    has_l: num(),
    /* Per-size live qty, e.g. 'S:1 M:2 L:1', smallest → largest. */
    size_split: z.string().optional(),
    brand_store_avg_size_depth: numNull(),
    /* RPH for the selected week (w0) and the two completed weeks before it,
       plus the revenue / denominator behind each so the UI can show the math. */
    rph_w0: numNull().optional(),
    rph_w1: numNull(),
    rph_w2: numNull(),
    rph_rev_w0: numNull().optional(),
    rph_rev_w1: numNull().optional(),
    rph_rev_w2: numNull().optional(),
    /* Monday FRONT inventory (NULL when nothing fronted — RPH undefined). */
    rph_denom_w0: numNull().optional(),
    rph_denom_w1: numNull().optional(),
    rph_denom_w2: numNull().optional(),
    /* Weekly sold qty + STR (sold ÷ (sold + closing stock)) per week. Closing:
       live stock for W, the following Monday's snapshot for W-1 / W-2. */
    qty_w0: numNull().optional(),
    qty_w1: numNull().optional(),
    qty_w2: numNull().optional(),
    str_w0: numNull().optional(),
    str_w1: numNull().optional(),
    str_w2: numNull().optional(),
    str_closing_w0: numNull().optional(),
    str_closing_w1: numNull().optional(),
    str_closing_w2: numNull().optional(),
    first_grn_date: z.string().nullable(),
    bottom_rph_flag: num(),
    broken_stock_flag: num(),
    /* Component flags so the UI can say WHICH broken-stock reason fired. */
    broken_stock_low_depth_flag: num().optional(),
    broken_stock_skew_flag: num().optional(),
  }),
);
export type StyleStoreSplit = z.infer<typeof StyleStoreSplitSchema>;
